import { Action, State, StateContext } from '@ngxs/store';
import {
  AddAdminAction,
  ArchiveGroupAction,
  AssignTrainerAction,
  GetGroupByIdAction,
  KickUserAction,
  LeaveGroupAction,
  RemoveAdminAction,
  RemovePendingUserFromListAction,
  RemoveTrainerAction,
  RequestToJoinGroupAction,
  TransferGroupOwnershipAction,
  UnarchiveGroupAction,
} from './group-content.actions';
import { GroupDetailsDto, GroupsService, LeaveGroupRequestParams } from '@smatchy/sdk';
import { inject, Injectable } from '@angular/core';
import { catchError, map } from 'rxjs';
import { aFailedCallIsReportedAndStops } from '../../../utils/a-failed-call-is-reported-and-stops';

import { Router } from '@angular/router';
import { ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { GetMyGroupsAction } from './my-groups.actions';
import { AnalyticsService } from '../../../services/analytics.service';

// SMA-185 Q7. `already_trainer` / `not_trainer` are races, not mistakes, like
// the archive ones: the server was already where the tap wanted it. We reload
// the group right after so the screen catches up; the message only says why
// nothing visible changed. `owner_not_pro` should not normally be reachable,
// since the action is hidden unless ownerIsPro, but it is surfaced plainly in
// case a group loses its pro status between the read and the tap. Exported so
// the handler spec asserts the routing through the same map the handler reads.
export const TRAINER_ERROR_TRANSLATIONS: Record<string, string> = {
  group_not_found: 'GROUP.GROUP_NOT_FOUND',
  forbidden: 'GROUP_DETAILS_PAGE.TRAINER.FORBIDDEN',
  owner_not_pro: 'GROUP_DETAILS_PAGE.TRAINER.OWNER_NOT_PRO',
  not_a_member: 'GROUP_DETAILS_PAGE.TRAINER.NOT_A_MEMBER',
  already_trainer: 'GROUP_DETAILS_PAGE.TRAINER.ALREADY_TRAINER',
  not_trainer: 'GROUP_DETAILS_PAGE.TRAINER.NOT_TRAINER',
};

export interface GroupContentStateModel extends Omit<GroupDetailsDto, 'sport' | 'location'> {
  ownershipTransferLoading: boolean;

  /*
   * TWO FIELDS THE DEFAULTS CLEAR WITH NULL, declared here rather than in the
   * generated DTO, which regeneration would overwrite. GroupDetailsDto
   * describes a group that EXISTS, so it declares both as always present; this
   * state also has to describe the moment before one is loaded, where the
   * defaults below set null for each.
   */
  sport?: GroupDetailsDto['sport'] | null;
  location?: GroupDetailsDto['location'] | null;
}

const defaults: GroupContentStateModel = {
  _id: '',
  id: '',
  name: '',
  backgroundUrl: '',
  description: '',
  sport: null,
  // No group is loaded yet, so it plays no sport. An empty list rather than
  // null: every screen reads this field with a loop, and a loop over nothing
  // draws nothing, which is what an unloaded group should draw.
  sports: [],
  nbUsers: 0,
  location: null,
  privacy: GroupDetailsDto.PrivacyEnum.Public,
  rightCreateActivity: GroupDetailsDto.RightCreateActivityEnum.AllMembers,
  websiteUrl: '',
  members: [],
  owners: [],
  pendingInviteUsers: [],
  isAdmin: false,
  isOwner: false,
  isArchived: false,
  imAmPending: false,
  haveJoined: false,
  admins: [],
  // SMA-185 Q7. No group loaded yet: no trainers, and the trainer action stays
  // hidden until the server says the owner is a validated pro.
  trainers: [],
  ownerIsPro: false,
  allowActivitySharing: true,
  canPublish: true,
  ownershipTransferLoading: false,
};

@State<GroupContentStateModel>({
  name: 'groupContent',
  defaults,
})
@Injectable()
export class GroupContentState {
  groupService = inject(GroupsService);
  router = inject(Router);
  toastController = inject(ToastController);
  translate = inject(TranslateService);
  private readonly _analytics = inject(AnalyticsService);
  private readonly leaveErrorTranslations: Record<string, string> = {
    group_missing_admin: 'GROUP_DETAILS_PAGE.NO_ADMIN_AVAILABLE',
    group_invalid_admin: 'GROUP_DETAILS_PAGE.SELECT_NEW_OWNER_INVALID',
    group_next_owner_required: 'GROUP_DETAILS_PAGE.NO_ADMIN_AVAILABLE',
    group_invalid_next_owner: 'GROUP_DETAILS_PAGE.SELECT_NEW_OWNER_INVALID',
    ownership_transfer_conflict: 'OWNERSHIP_TRANSFER.CONFLICT',
  };
  private readonly ownershipTransferErrorTranslations: Record<string, string> = {
    group_invalid_next_owner: 'GROUP_DETAILS_PAGE.SELECT_NEW_OWNER_INVALID',
    group_not_found: 'GROUP.GROUP_NOT_FOUND',
    forbidden: 'OWNERSHIP_TRANSFER.OWNER_ONLY',
    ownership_transfer_conflict: 'OWNERSHIP_TRANSFER.CONFLICT',
  };
  // SMA-185 Q6. `already_archived` / `not_archived` are races, not mistakes:
  // someone else changed the state first. We reload the group right after, so
  // the screen catches up to the truth; the message only says why nothing
  // visible happened on this tap.
  private readonly archiveErrorTranslations: Record<string, string> = {
    group_not_found: 'GROUP.GROUP_NOT_FOUND',
    forbidden: 'GROUP_DETAILS_PAGE.ARCHIVE.FORBIDDEN',
    already_archived: 'GROUP_DETAILS_PAGE.ARCHIVE.ALREADY_ARCHIVED',
    not_archived: 'GROUP_DETAILS_PAGE.ARCHIVE.NOT_ARCHIVED',
  };

  @Action(GetGroupByIdAction)
  getGroupById({ setState, getState }: StateContext<GroupContentStateModel>, { id }: GetGroupByIdAction) {
    return this.groupService.getGroupById({ id, hasViewer: true }).pipe(
      map(async ({ error, value }) => {
        if (error === 'group_not_found') {
          await this.presentGroupNotFoundError();
          return;
        }
        // Any other error left the page blank with no explanation. A missing
        // group has its own message above; everything else gets the generic one.
        if (error) {
          await this.presentDefaultErrorToast();
          return;
        }
        if (value?.group) {
          // No group_view logged here on purpose. This action is a data read, not
          // a visit: the group page dispatches it twice on every arrival, and again
          // after a member is kicked, an admin changes, an edit is saved or the page
          // is pulled to refresh. Counting reads counted a single visit twice, and an
          // administered group many times over. The page owns the event now, once per
          // arrival, the way the activity page already did it.
          // Ensure optional fields missing from API response don't leak from previous group.
          setState({ ...getState(), parentGroupId: undefined, websiteUrl: '', ...value.group });
        }
      }),
      catchError((err) => aFailedCallIsReportedAndStops(err, () => this.presentDefaultErrorToast())),
    );
  }

  private async presentGroupNotFoundError() {
    const toast = await this.toastController.create({
      message: this.translate.instant('GROUP.GROUP_NOT_FOUND'),
      duration: 3000,
      icon: 'warning',
      position: 'top',
      color: 'danger',
    });
    await toast.present();
    this.router.navigate(['/home']);
  }

  @Action(RequestToJoinGroupAction)
  RequestToJoinGroupAction(
    { setState, getState }: StateContext<GroupContentStateModel>,
    { message, modalToClose }: RequestToJoinGroupAction,
  ) {
    return this.groupService.requestToJoinGroupAction({ id: getState()._id, requestToJoinGroupDto: { message } }).pipe(
      map(async ({ value }) => {
        // A 200 with success:false is still a failure: the request did not go
        // through. Without this branch the modal just sat there, saying nothing,
        // and the member had no idea whether to try again. Keep the modal open so
        // they can, and tell them it did not work.
        if (!value?.success) {
          await this.presentDefaultErrorToast();
          return;
        }
        const group = getState();
        // A request, not a join: the next line sets imAmPending. Emitting
        // group_join here counted requests as memberships.
        this._analytics.logGroupJoinRequest({
          groupId: group._id,
          sport: group.sport?.id,
          city: group.location?.name,
          memberCount: group.nbUsers,
        });
        modalToClose.dismiss(null, 'cancel');
        setState({ ...getState(), imAmPending: true });
        const toast = await this.toastController.create({
          message: this.translate.instant('GROUPS.JOIN_GROUP.SUCCESS_TOASTER_MESSAGE'),
          duration: 1500,
          icon: 'checkmark',
          position: 'top',
        });

        await toast.present();
      }),
      catchError((error) => aFailedCallIsReportedAndStops(error, () => this.presentDefaultErrorToast())),
    );
  }

  @Action(LeaveGroupAction)
  LeaveGroupAction({ getState }: StateContext<GroupContentStateModel>, { nextOwnerId }: LeaveGroupAction) {
    const request: LeaveGroupRequestParams = { id: getState()._id };
    if (nextOwnerId) {
      request.leaveGroupDto = { nextOwnerId };
    }

    return this.groupService.leaveGroup(request).pipe(
      map(async ({ error, value }) => {
        if (error) {
          const errorCode = typeof error === 'string' ? error : undefined;
          await this.presentLeaveErrorToast(errorCode);
          return;
        }

        if (value?.success) {
          const toast = await this.toastController.create({
            message: this.translate.instant('GROUPS.LEAVE_GROUP.SUCCESS_TOASTER_MESSAGE'),
            duration: 1500,
            icon: 'checkmark',
            position: 'top',
          });

          await toast.present();
          return;
        }

        await this.presentLeaveErrorToast();
      }),
      catchError((err) => aFailedCallIsReportedAndStops(err, () => this.presentLeaveErrorToast())),
    );
  }

  private async presentDefaultErrorToast(): Promise<void> {
    const toast = await this.toastController.create({
      message: this.translate.instant('GENERICS.LABELS.DEFAULT_ERROR'),
      duration: 3000,
      icon: 'warning',
      position: 'top',
      color: 'danger',
    });
    await toast.present();
  }

  private async presentLeaveErrorToast(errorCode?: string) {
    const translationKey = this.leaveErrorTranslations[errorCode ?? ''] || 'GENERICS.LABELS.DEFAULT_ERROR';
    const toast = await this.toastController.create({
      message: this.translate.instant(translationKey),
      duration: 3000,
      icon: 'warning',
      position: 'top',
      color: 'danger',
    });

    await toast.present();
  }

  @Action(TransferGroupOwnershipAction)
  transferGroupOwnership(
    { patchState, dispatch }: StateContext<GroupContentStateModel>,
    { groupId, nextOwnerId }: TransferGroupOwnershipAction,
  ) {
    patchState({ ownershipTransferLoading: true });
    return this.groupService
      .transferGroupOwnership({
        id: groupId,
        transferOwnershipDto: { nextOwnerId },
      })
      .pipe(
        map(async ({ error, value }) => {
          patchState({ ownershipTransferLoading: false });
          if (error || !value?.success) {
            const errorCode = typeof error === 'string' ? error : undefined;
            await this.presentOwnershipTransferErrorToast(errorCode);
            if (errorCode === 'ownership_transfer_conflict') {
              dispatch(new GetGroupByIdAction(groupId));
            }
            return;
          }

          const toast = await this.toastController.create({
            message: this.translate.instant('OWNERSHIP_TRANSFER.SUCCESS_GROUP'),
            duration: 2000,
            icon: 'checkmark',
            position: 'top',
          });
          await toast.present();
          dispatch(new GetGroupByIdAction(groupId));
          dispatch(new GetMyGroupsAction());
          this.router.navigate(['/home/group', groupId]);
        }),
        catchError((error) => {
          patchState({ ownershipTransferLoading: false });
          return aFailedCallIsReportedAndStops(error, () => this.presentOwnershipTransferErrorToast());
        }),
      );
  }

  private async presentOwnershipTransferErrorToast(errorCode?: string) {
    const translationKey = this.ownershipTransferErrorTranslations[errorCode ?? ''] || 'GENERICS.LABELS.DEFAULT_ERROR';
    const toast = await this.toastController.create({
      message: this.translate.instant(translationKey),
      duration: 3000,
      icon: 'warning',
      position: 'top',
      color: 'danger',
    });
    await toast.present();
  }

  @Action(KickUserAction)
  kickUserAction(_ctx: StateContext<GroupContentStateModel>, { userId, groupId }: KickUserAction) {
    return this.groupService.kickUserFromGroup({ id: groupId, userId }).pipe(
      map(async ({ value }) => {
        if (value?.success) {
          const toast = await this.toastController.create({
            message: this.translate.instant('MEMBER_GROUP_MODAL.KICK_USER_SUCCESS'),
            duration: 1500,
            icon: 'bookmark-outline',
            cssClass: 'kick-user-toast',
            position: 'top',
          });

          await toast.present();
        }
      }),
      catchError((error) => aFailedCallIsReportedAndStops(error, () => this.presentDefaultErrorToast())),
    );
  }

  @Action(AddAdminAction)
  addAdminAction(_ctx: StateContext<GroupContentStateModel>, { userId, groupId }: KickUserAction) {
    return this.groupService.addAdminToGroup({ id: groupId, userId }).pipe(
      map(async ({ value }) => {
        if (!value?.added) {
          await this.presentDefaultErrorToast();
          return;
        }
        const toast = await this.toastController.create({
          message: this.translate.instant('MEMBER_GROUP_MODAL.ADD_ADMIN_SUCCESS'),
          duration: 1500,
          icon: 'checkmark',
          position: 'top',
        });

        await toast.present();
      }),
      catchError((error) => aFailedCallIsReportedAndStops(error, () => this.presentDefaultErrorToast())),
    );
  }

  @Action(RemoveAdminAction)
  removeAdminAction(_ctx: StateContext<GroupContentStateModel>, { userId, groupId }: KickUserAction) {
    return this.groupService.removeAdminToGroup({ id: groupId, userId }).pipe(
      map(async ({ value }) => {
        if (!value?.success) {
          await this.presentDefaultErrorToast();
          return;
        }
        const toast = await this.toastController.create({
          message: this.translate.instant('MEMBER_GROUP_MODAL.REMOVE_ADMIN_SUCCESS'),
          duration: 1500,
          icon: 'checkmark',
          position: 'top',
        });

        await toast.present();
      }),
      catchError((error) => aFailedCallIsReportedAndStops(error, () => this.presentDefaultErrorToast())),
    );
  }

  @Action(ArchiveGroupAction)
  archiveGroupAction({ dispatch }: StateContext<GroupContentStateModel>, { groupId }: ArchiveGroupAction) {
    return this.groupService.archiveGroup({ id: groupId }).pipe(
      map(async ({ error }) => {
        if (error) {
          const errorCode = typeof error === 'string' ? error : undefined;
          await this.presentArchiveErrorToast(errorCode);
          // Reload even on error: a race (already_archived) means the server is
          // already where the user wanted it, and the screen should show that.
          dispatch(new GetGroupByIdAction(groupId));
          return;
        }
        await this.presentArchiveSuccessToast('GROUP_DETAILS_PAGE.ARCHIVE.ARCHIVED_TOAST');
        // The group reload flips the screen into its read-only state; the groups
        // list reload lets any archived marker there follow along.
        dispatch(new GetGroupByIdAction(groupId));
        dispatch(new GetMyGroupsAction());
      }),
      catchError((err) => aFailedCallIsReportedAndStops(err, () => this.presentArchiveErrorToast())),
    );
  }

  @Action(UnarchiveGroupAction)
  unarchiveGroupAction({ dispatch }: StateContext<GroupContentStateModel>, { groupId }: UnarchiveGroupAction) {
    return this.groupService.unarchiveGroup({ id: groupId }).pipe(
      map(async ({ error }) => {
        if (error) {
          const errorCode = typeof error === 'string' ? error : undefined;
          await this.presentArchiveErrorToast(errorCode);
          dispatch(new GetGroupByIdAction(groupId));
          return;
        }
        await this.presentArchiveSuccessToast('GROUP_DETAILS_PAGE.ARCHIVE.REACTIVATED_TOAST');
        dispatch(new GetGroupByIdAction(groupId));
        dispatch(new GetMyGroupsAction());
      }),
      catchError((err) => aFailedCallIsReportedAndStops(err, () => this.presentArchiveErrorToast())),
    );
  }

  private async presentArchiveSuccessToast(translationKey: string): Promise<void> {
    const toast = await this.toastController.create({
      message: this.translate.instant(translationKey),
      duration: 2000,
      icon: 'checkmark',
      position: 'top',
    });
    await toast.present();
  }

  private async presentArchiveErrorToast(errorCode?: string): Promise<void> {
    const translationKey = this.archiveErrorTranslations[errorCode ?? ''] || 'GENERICS.LABELS.DEFAULT_ERROR';
    const toast = await this.toastController.create({
      message: this.translate.instant(translationKey),
      duration: 3000,
      icon: 'warning',
      position: 'top',
      color: 'danger',
    });
    await toast.present();
  }

  @Action(AssignTrainerAction)
  assignTrainerAction({ dispatch }: StateContext<GroupContentStateModel>, { userId, groupId }: AssignTrainerAction) {
    return this.groupService.addTrainerToGroup({ id: groupId, userId }).pipe(
      map(async ({ error }) => {
        if (error) {
          const errorCode = typeof error === 'string' ? error : undefined;
          await this.presentTrainerErrorToast(errorCode);
          // Reload even on error: a race (already_trainer) means the server is
          // already where the user wanted it, and the screen should show that.
          dispatch(new GetGroupByIdAction(groupId));
          return;
        }
        await this.presentTrainerSuccessToast('GROUP_DETAILS_PAGE.TRAINER.ASSIGNED_TOAST');
        // The group reload refreshes trainers[]; the groups list reload keeps any
        // badge there in step.
        dispatch(new GetGroupByIdAction(groupId));
        dispatch(new GetMyGroupsAction());
      }),
      catchError((err) => aFailedCallIsReportedAndStops(err, () => this.presentTrainerErrorToast())),
    );
  }

  @Action(RemoveTrainerAction)
  removeTrainerAction({ dispatch }: StateContext<GroupContentStateModel>, { userId, groupId }: RemoveTrainerAction) {
    return this.groupService.removeTrainerFromGroup({ id: groupId, userId }).pipe(
      map(async ({ error }) => {
        if (error) {
          const errorCode = typeof error === 'string' ? error : undefined;
          await this.presentTrainerErrorToast(errorCode);
          dispatch(new GetGroupByIdAction(groupId));
          return;
        }
        await this.presentTrainerSuccessToast('GROUP_DETAILS_PAGE.TRAINER.REMOVED_TOAST');
        dispatch(new GetGroupByIdAction(groupId));
        dispatch(new GetMyGroupsAction());
      }),
      catchError((err) => aFailedCallIsReportedAndStops(err, () => this.presentTrainerErrorToast())),
    );
  }

  private async presentTrainerSuccessToast(translationKey: string): Promise<void> {
    const toast = await this.toastController.create({
      message: this.translate.instant(translationKey),
      duration: 2000,
      icon: 'checkmark',
      position: 'top',
    });
    await toast.present();
  }

  private async presentTrainerErrorToast(errorCode?: string): Promise<void> {
    const translationKey = TRAINER_ERROR_TRANSLATIONS[errorCode ?? ''] || 'GENERICS.LABELS.DEFAULT_ERROR';
    const toast = await this.toastController.create({
      message: this.translate.instant(translationKey),
      duration: 3000,
      icon: 'warning',
      position: 'top',
      color: 'danger',
    });
    await toast.present();
  }

  @Action(RemovePendingUserFromListAction)
  removePendingUserFromListAction(
    { patchState, getState }: StateContext<GroupContentStateModel>,
    { userId }: RemovePendingUserFromListAction,
  ) {
    patchState({ pendingInviteUsers: getState().pendingInviteUsers.filter((user) => user.id !== userId) });
  }
}
